import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { apiClient } from '@/services/api';
import type { FeedbackStatus } from '@/types';

export function useFeedback() {
  const { sessionId } = useParams();
  const updateMessageFeedback = useAppStore((state) => state.updateMessageFeedback);

  const submitFeedback = useCallback(
    async (messageId: string, feedbackType: 'like' | 'dislike') => {
      if (!sessionId) {
        console.error('No session ID available for feedback');
        return;
      }

      try {
        // Optimistic update - update UI immediately
        updateMessageFeedback(messageId, {
          type: feedbackType,
          timestamp: new Date(),
        });

        // Submit to API
        await apiClient.submitFeedback(sessionId, messageId, feedbackType);
        
        console.log('Feedback submitted successfully:', { messageId, feedbackType });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        
        // Revert optimistic update on error
        updateMessageFeedback(messageId, { type: null });
        
        // TODO: Show error toast notification
        throw error;
      }
    },
    [sessionId, updateMessageFeedback]
  );

  const removeFeedback = useCallback(
    async (messageId: string) => {
      if (!sessionId) {
        console.error('No session ID available for feedback removal');
        return;
      }

      try {
        // Optimistic update - remove feedback immediately
        updateMessageFeedback(messageId, { type: null });

        // Clear feedback from backend
        await apiClient.clearFeedback(sessionId, messageId);
        
        console.log('Feedback removed successfully:', { messageId });
      } catch (error) {
        console.error('Failed to remove feedback:', error);
        throw error;
      }
    },
    [sessionId, updateMessageFeedback]
  );

  const loadFeedback = useCallback(
    async (messageId: string): Promise<FeedbackStatus | null> => {
      if (!sessionId) {
        return null;
      }

      try {
        const feedback = await apiClient.getFeedback(sessionId, messageId);
        return feedback.type ? feedback : null;
      } catch (error) {
        console.error('Failed to load feedback:', error);
        return null;
      }
    },
    [sessionId]
  );

  const toggleFeedback = useCallback(
    async (messageId: string, feedbackType: 'like' | 'dislike', currentFeedback?: FeedbackStatus) => {
      // If clicking the same feedback type, remove it; otherwise, set the new type
      if (currentFeedback?.type === feedbackType) {
        await removeFeedback(messageId);
      } else {
        await submitFeedback(messageId, feedbackType);
      }
    },
    [submitFeedback, removeFeedback]
  );

  return {
    submitFeedback,
    removeFeedback,
    loadFeedback,
    toggleFeedback,
  };
}